import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Menu, X, User, LogOut, LayoutDashboard, TrendingUp, Palette, Camera, ShoppingBag, DollarSign, Users, Info, BookOpen, Wallet, Copy, Coins, Building2, GraduationCap, Calendar, Settings, BarChart3, Bot, Zap, Server, BookMarked } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_legacy-trader/artifacts/6c7ukjms_Copy%20of%20Copy%20of%20NRL%20Graphic%20Logo%201.png";

// External links
const EXTERNAL_LINKS = {
  shop: 'https://nrluniversity.com/shop',
  store: 'https://nrluniversity.com/shop',
  university: 'https://nrluniversity.com',
  projectPiLabs: 'https://projectpilabs.com',
  liquidAgent: 'https://liquidagent.ai',
  piPools: 'https://www.pipools.xyz',
};

// Primary navigation for logged-in users
const navLinks = [
  { path: '/ai-assistant', label: 'AI Assistant', icon: Bot, requiresAuth: true },
  { path: '/the-wealth-lexicon', label: 'Book', icon: BookMarked, requiresAuth: false },
  { path: '/web3-services', label: 'Web3', icon: Zap, requiresAuth: false },
  { path: '/booking', label: 'Book Consult', icon: Calendar, requiresAuth: false },
  { path: 'external:shop', label: 'Shop', icon: ShoppingBag, requiresAuth: false, external: true },
  { path: '/gilded-study', label: 'Study', icon: BookOpen, requiresAuth: false },
  { path: '/about', label: 'About', icon: Info, requiresAuth: false },
];

// Trading features (restricted access)
const tradingLinks = [
  { path: '/trading', label: 'Trading Terminal', icon: TrendingUp },
  { path: '/portfolio-analytics', label: 'Portfolio Analytics', icon: BarChart3 },
  { path: '/copy-trading', label: 'Copy Trading', icon: Copy },
  { path: '/staking', label: 'Staking', icon: Coins },
  { path: '/real-estate', label: 'Real Estate', icon: Building2 },
  { path: '/trade-journal', label: 'Trade Journal', icon: BookOpen },
];

// More links for dropdown
const moreLinks = [
  { path: '/wallet', label: 'Wallet', icon: Wallet },
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
];

export function Navbar() {
  const { user, isAuthenticated, login, logout } = useAuth();
  const { theme, updateThemeForRoute } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  // Update theme based on route
  useEffect(() => {
    updateThemeForRoute(location.pathname);
  }, [location.pathname, updateThemeForRoute]);
  
  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };
  
  const isActive = (path) => location.pathname === path;
  
  // Hide navbar on landing page when not scrolled
  const isLanding = location.pathname === '/';
  
  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isLanding 
          ? 'bg-[#1c1814]/95 backdrop-blur-md border-b border-[#D4AF37]/20' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 group"
            data-testid="logo-link"
          >
            <div className="relative">
              <img 
                src={LOGO_URL} 
                alt="Nouveau Riche Legacy" 
                className="h-12 w-12 object-contain transition-transform duration-300 group-hover:scale-110"
              />
            </div>
            <div className="hidden sm:flex flex-col">
              <span className="font-display text-lg text-[#FAFAFA] leading-tight">Nouveau Riche</span>
              <span className="font-display text-sm text-[#D4AF37] leading-tight">Legacy</span>
            </div>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks
              .filter(link => !link.requiresAuth || isAuthenticated)
              .map((link) => (
                link.external ? (
                  <a
                    key={link.path}
                    href={EXTERNAL_LINKS.shop}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`nav-shop`}
                    className="nav-link text-sm font-body uppercase tracking-wider transition-colors duration-300 text-[#FAFAFA] hover:text-[#D4AF37]"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`nav-${link.path.slice(1).replace('/', '-')}`}
                    className={`nav-link text-sm font-body uppercase tracking-wider transition-colors duration-300 ${
                      isActive(link.path) 
                        ? 'text-[#D4AF37]' 
                        : 'text-[#FAFAFA] hover:text-[#D4AF37]'
                    }`}
                  >
                    {link.label}
                  </Link>
                )
            ))}
            
            {/* More dropdown for authenticated users */}
            {isAuthenticated && (
              <>
                {/* Trading dropdown - shows trading features (restricted) */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="text-[#FAFAFA] hover:text-[#D4AF37] text-sm uppercase tracking-wider">
                      <TrendingUp className="h-4 w-4 mr-1" /> Trading
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-52 bg-[#1c1814] border-[#D4AF37]/20">
                    <div className="px-2 py-1.5 border-b border-[#D4AF37]/10">
                      <p className="text-xs text-gray-400">Trading Features</p>
                    </div>
                    {tradingLinks.map((link) => (
                      <DropdownMenuItem 
                        key={link.path}
                        onClick={() => navigate(link.path)}
                        className="text-[#FAFAFA] focus:bg-[#D4AF37]/20 focus:text-[#D4AF37] cursor-pointer"
                      >
                        <link.icon className="mr-2 h-4 w-4" />
                        {link.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* More dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="text-[#FAFAFA] hover:text-[#D4AF37] text-sm uppercase tracking-wider">
                      More
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-48 bg-[#1c1814] border-[#D4AF37]/20">
                    {moreLinks.map((link) => (
                      <DropdownMenuItem 
                        key={link.path}
                        onClick={() => navigate(link.path)}
                        className="text-[#FAFAFA] focus:bg-[#D4AF37]/20 focus:text-[#D4AF37] cursor-pointer"
                      >
                        <link.icon className="mr-2 h-4 w-4" />
                        {link.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
          
          {/* Auth Section */}
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="relative h-10 w-10 rounded-full"
                    data-testid="user-menu-trigger"
                  >
                    <Avatar className="h-10 w-10 border-2 border-[#D4AF37]">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback className="bg-[#D4AF37] text-[#1c1814] font-bold">
                        {user?.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  className="w-56 bg-[#1c1814] border-[#D4AF37]/20" 
                  align="end"
                >
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium text-[#FAFAFA]">{user?.name}</p>
                    <p className="text-xs text-[#FAFAFA]/60">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator className="bg-[#D4AF37]/20" />
                  <DropdownMenuItem 
                    onClick={() => navigate('/dashboard')}
                    className="text-[#FAFAFA] focus:bg-[#D4AF37]/20 focus:text-[#D4AF37] cursor-pointer"
                    data-testid="menu-dashboard"
                  >
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/portfolio')}
                    className="text-[#FAFAFA] focus:bg-[#D4AF37]/20 focus:text-[#D4AF37] cursor-pointer"
                    data-testid="menu-portfolio"
                  >
                    <TrendingUp className="mr-2 h-4 w-4" />
                    Portfolio
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/profile')}
                    className="text-[#FAFAFA] focus:bg-[#D4AF37]/20 focus:text-[#D4AF37] cursor-pointer"
                    data-testid="menu-profile"
                  >
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[#D4AF37]/20" />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-red-400 focus:bg-red-500/20 focus:text-red-400 cursor-pointer"
                    data-testid="menu-logout"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login">
                <Button 
                  className="btn-gold text-sm px-6 py-2"
                  data-testid="login-button"
                >
                  Sign In
                </Button>
              </Link>
            )}
            
            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden text-[#FAFAFA] p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#1c1814] border-t border-[#D4AF37]/20">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              link.external ? (
                <a
                  key={link.path}
                  href={EXTERNAL_LINKS.shop}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 py-2 text-sm font-body uppercase tracking-wider transition-colors duration-300 text-[#FAFAFA] hover:text-[#D4AF37]"
                >
                  <link.icon size={18} />
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 py-2 text-sm font-body uppercase tracking-wider transition-colors duration-300 ${
                    isActive(link.path) 
                      ? 'text-[#D4AF37]' 
                      : 'text-[#FAFAFA] hover:text-[#D4AF37]'
                  }`}
                >
                  <link.icon size={18} />
                  {link.label}
                </Link>
              )
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
